import { canonicalFeedbackPreviewEnabled } from '@/features/feedback-integration/feedbackPreview';
import { CanonicalFeedbackCollection } from '@/features/feedback-integration/CanonicalFeedbackCollection';
import type { FeedbackConversationTarget } from '@/lib/types';
import { useEffect, useMemo, useState } from "react";
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { ResourceLibrary } from '@/features/resource-library/ResourceLibrary';
import { CanonicalCoachRecaps } from '@/features/recaps/CanonicalCoachRecaps';
import { useRecapScope } from '@/features/recaps/useCanonicalRecap';
import { useCanonicalScope } from '@/features/feedback-integration/useCanonicalFeedback';
import { useCanonicalCoachWorkspace } from '@/features/feedback-integration/CanonicalCoachWorkspaceHost';
import { StaffFeedbackRead } from '@/features/feedback-integration/StaffFeedbackRead';
import { useMatches } from '@/features/matches/useMatches';
import { getAppSettings } from '@/lib/api';
import { publicAppSettingsKey } from '@/lib/queryClient';
import { getUploadedClubLogoUrl } from '@/lib/matchLogos';
import { useScopeStore } from '@/stores/scope';
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ClipboardList,
  FileText,
  Film,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import { PageHeader, PageHeaderActionsContext } from "./components/PageHeader";
import { NotesPage } from "./NotesPage";
import { ClipsPage } from "./ClipsPage";
import { MatchSummariesPage } from "./MatchSummariesPage";
import type { GoalComposerSource } from "./components/GoalComposerModal";

export type FeedbackLibrarySection =
  "notes" | "observations" | "clips" | "recaps";

const SECTIONS: ReadonlyArray<{
  id: FeedbackLibrarySection;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    id: "notes",
    label: "Notes",
    description: "Player-facing teaching points and coach notes.",
    icon: FileText,
  },
  {
    id: "observations",
    label: "Observations",
    description: "Tactical-board and non-video observations.",
    icon: ClipboardList,
  },
  {
    id: "clips",
    label: "Clips",
    description: "Saved video evidence windows.",
    icon: Film,
  },
  {
    id: "recaps",
    label: "Match Recaps",
    description: "Team-level match summaries and linked evidence.",
    icon: ScrollText,
  },
];

export type FeedbackLibraryPageProps = {
  onOpenFeedback?: (target: FeedbackConversationTarget) => void;
  onResumeFeedback?: (feedbackId: string) => void;
  onGiveFeedback?: () => void;
  onCreateFeedbackGoal?: (feedbackId: string) => void;
  onCreatePlayerGoal?: (playerId: string, source: GoalComposerSource) => void;
};

export function FeedbackLibraryPage({
  onCreatePlayerGoal, onOpenFeedback, onResumeFeedback, onGiveFeedback, onCreateFeedbackGoal,
}: FeedbackLibraryPageProps) {
  const [actionTarget, setActionTarget] = useState<HTMLDivElement | null>(null);
  const navigate = useNavigate({ from: "/coach" });
  const search = useSearch({ from: "/coach" });
  const section = resolveLibrarySection(search.tab, search.library);

  const selectSection = (next: FeedbackLibrarySection) => {
    void navigate({
      to: "/coach",
      search: { tab: "library", library: next },
    });
  };

  if (canonicalFeedbackPreviewEnabled() && onOpenFeedback) return <CanonicalLibrary onOpenFeedback={onOpenFeedback}
    onResumeFeedback={onResumeFeedback} onGiveFeedback={onGiveFeedback} onCreateFeedbackGoal={onCreateFeedbackGoal}/>;

  return (
    <div className="flex flex-col gap-5" data-testid="feedback-library-page">
      <PageHeader
        eyebrow="Saved coaching"
        title="Feedback Library"
        actions={
          <div
            ref={setActionTarget}
            className="flex flex-wrap items-center gap-2"
          />
        }
        description="One place to find coach-created feedback objects: notes, observations, clips, and match recaps."
      />

      <section
        className="-mx-1 overflow-x-auto px-1"
        aria-label="Feedback library sections"
        data-testid="feedback-library-sections"
      >
        <div className="inline-flex min-w-full gap-1 rounded border border-rule bg-surface p-1 shadow-sm">
          {SECTIONS.map((item) => {
            const selected = item.id === section;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => selectSection(item.id)}
                aria-pressed={selected}
                title={item.description}
                data-testid={`feedback-library-section-${item.id}`}
                className={[
                  "inline-flex h-9 flex-1 items-center justify-center gap-2 rounded px-3 text-sm font-display font-semibold uppercase tracking-wider transition-colors",
                  selected
                    ? "bg-accent text-accent-ink"
                    : "text-ink-muted hover:bg-field hover:text-ink",
                ].join(" ")}
              >
                <Icon size={16} strokeWidth={2} aria-hidden="true" />
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <PageHeaderActionsContext.Provider value={actionTarget}>
        <div data-testid={`feedback-library-active-${section}`}>
          {section === "notes" ? (
            <NotesPage
              key="notes"
              mode="notes"
              onCreatePlayerGoal={onCreatePlayerGoal}
            />
          ) : section === "observations" ? (
            <NotesPage
              key="observations"
              mode="observations"
              onCreatePlayerGoal={onCreatePlayerGoal}
            />
          ) : section === "clips" ? (
            <ClipsPage onCreatePlayerGoal={onCreatePlayerGoal} />
          ) : (
            <MatchSummariesPage />
          )}
        </div>
      </PageHeaderActionsContext.Provider>
    </div>
  );
}

function CanonicalLibrary({onOpenFeedback,onResumeFeedback,onGiveFeedback,onCreateFeedbackGoal}:FeedbackLibraryPageProps & {onOpenFeedback:NonNullable<FeedbackLibraryPageProps['onOpenFeedback']>}){
  const navigate=useNavigate({from:'/coach'}),search=useSearch({from:'/coach'}),workspace=useCanonicalCoachWorkspace();
  const scope=useCanonicalScope('coach'),recapScope=useRecapScope('coach'),matches=useMatches();
  const entryKey=useMemo(()=>crypto.randomUUID(),[recapScope]);
  const settings=useQuery({queryKey:publicAppSettingsKey(),queryFn:getAppSettings,staleTime:60_000});
  const teamName=useScopeStore(state=>state.active?.team.name),[staffId,setStaffId]=useState<string|null>(null);
  const [resourceOverlay,setResourceOverlay]=useState(false);
  const [actionTarget,setActionTarget]=useState<HTMLDivElement|null>(null);
  useEffect(()=>{workspace?.setOtherOverlay(!!staffId||resourceOverlay);return()=>workspace?.setOtherOverlay(false);},[staffId,resourceOverlay,workspace?.setOtherOverlay]);
  const section=search.library_kind??(search.library==='recaps'&&!search.library_item_id?'recaps':'feedback');
  useEffect(()=>setStaffId(null),[section,search.recap_id,entryKey]);
  const currentMatches=(matches.data??[]).filter(match=>match.season_id===recapScope?.seasonId);
  const sections=[{id:'feedback',label:'Feedback',icon:FileText},{id:'resources',label:'Resources',icon:Film},{id:'recaps',label:'Match Recaps',icon:ScrollText}] as const;
  return <div className="flex flex-col gap-5" data-testid="feedback-library-page">
    <PageHeader eyebrow="Saved coaching" title={section==='feedback'?'Feedback Library':`Feedback Library / ${section==='resources'?'Resources':'Match Recaps'}`} description="Feedback, independent saved Resources, and separate match recaps."
      actions={<div ref={setActionTarget} className="flex min-w-0 flex-wrap items-center gap-2">{section==='feedback'?<Button onClick={onGiveFeedback}>Give feedback</Button>:null}</div>}/>
    <section className="-mx-1 overflow-x-auto px-1" aria-label="Feedback library sections" data-testid="feedback-library-sections">
      <div className="inline-flex min-w-full gap-1 rounded border border-rule bg-surface p-1 shadow-sm">{sections.map(item=><button key={item.id} type="button"
        data-testid={`feedback-library-section-${item.id}`} aria-pressed={section===item.id}
        className={`inline-flex h-9 flex-1 items-center justify-center gap-2 rounded px-3 text-sm font-display font-semibold uppercase tracking-wider transition-colors ${section===item.id?'bg-accent text-accent-ink':'text-ink-muted hover:bg-field hover:text-ink'}`}
        onClick={()=>void navigate({to:'/coach',search:previous=>({...previous,tab:'library',library_kind:item.id,library:undefined,library_item_id:undefined,recap_id:undefined})})}>
        <item.icon size={16} aria-hidden/><span className="whitespace-nowrap">{item.label}</span></button>)}</div>
    </section>
    {!!search.library_item_id&&<p role="status" className="rounded-control border border-rule p-3 text-sm">This link refers to an older Library item. Choose a current item below; the old identifier has not been converted.</p>}
    <PageHeaderActionsContext.Provider value={actionTarget}>
    <div data-testid={`feedback-library-active-${section}`}>{section==='feedback'
      ?<CanonicalFeedbackCollection onOpen={onOpenFeedback} onResume={onResumeFeedback} onDraftChanges={onGiveFeedback} onCreateGoal={onCreateFeedbackGoal}/>
      :section==='resources'?scope&&workspace?<ResourceLibrary key={entryKey} scope={scope} state={workspace.resourceLibrary} onStateChange={workspace.setResourceLibrary}
        matches={currentMatches.map(match=>({id:match.id,label:`${match.home_team} vs ${match.away_team}`}))}
        onCreate={kind=>workspace.openResource(kind)} onEdit={workspace.openSavedResource} onLink={workspace.linkSavedResource} onAction={workspace.resourceAction}
        onOverlayChange={setResourceOverlay} recoveries={workspace.resourceRecoveries} onRecover={workspace.recoverResourceAction}
        linkRecoveries={workspace.resourceLinkRecoveries} onRecoverLink={workspace.recoverResourceLink}
        onOpenUse={usage=>{if(usage.availability==='available'){if(usage.publication_state==='draft')onResumeFeedback?.(usage.feedback_id);else setStaffId(usage.feedback_id);}}}/>
        :<p role="status">Choose an authorized coaching scope to open Resources.</p>
      :recapScope?<CanonicalCoachRecaps key={entryKey} scope={recapScope} matches={currentMatches} clubLogoUrl={getUploadedClubLogoUrl(settings.data)} tenantTeamName={teamName}/>
        :<p role="status">Choose an authorized coaching scope to open recaps.</p>}</div>
    </PageHeaderActionsContext.Provider>
    {staffId&&scope&&<StaffFeedbackRead scope={scope} feedbackId={staffId} onClose={()=>setStaffId(null)}/>}
  </div>;
}

function resolveLibrarySection(
  tab: string | undefined,
  library: FeedbackLibrarySection | undefined,
): FeedbackLibrarySection {
  if (tab === "notes") return "notes";
  if (tab === "clips") return "clips";
  if (tab === "summaries") return "recaps";
  return library ?? "notes";
}
